import { EventEmitter, Injectable } from '@angular/core';
import DocType from '../../interfaces/doc-type';
import { BehaviorSubject, Subscription } from 'rxjs';
import DocSubtype from '../../interfaces/doc-subtype';
import TaxReturnDifficultyLevels from '../../interfaces/tax-return-difficulty-levels';
import DocGroup from '../../interfaces/doc-group';
import DocTypeSubtype from '../../interfaces/doc-type-subtype';
import { Apollo, QueryRef } from 'apollo-angular';
import {
  CREATE_DOCUMENT,
  CreateDocumentInput,
  DELETE_DOCUMENT,
  DocSorting,
  DocumentFilter,
  DocumentRefreshResults,
  DocumentRefreshVariables,
  FETCH_DOCUMENT,
  FETCH_DOCUMENTS,
  REFRESH_DOCUMENTS,
  UPDATE_DOCUMENT,
  UPDATE_DUE_DATE,
  UpdateDocumentInput,
  UpdateDueDateVariables,
  WhereConditions,
} from './graphql/document-tracking';
import { Paginator, PaginatorInfo } from '../../types/paginator';
import DocStatus from '../../interfaces/doc-status';
import ID from '../../types/id';
import VDocumentsFilteringTable from '../../interfaces/v-documents-filtering-table';
import { CurrentUserService } from '../../services/current-user.service';
import { CREATE_NOTE } from './document-notes/graphql/document-notes';
import { Direction } from '../../types/relation-order-by';
import { DefaultSnackBarService } from "../../services/default-snack-bar.service";

const defaultSort: DocSorting[] = [
  {
    column: 'DOC_DATE',
    order: Direction.DESC,
  },
  { column: 'OWNED_ENTITY_NAME', order: Direction.ASC },
];

@Injectable({
  providedIn: 'root',
})
export class DocumentTrackingService {
  private _docTypes: DocType[] = [];
  private docTypes = new BehaviorSubject(this._docTypes);
  public docTypes$ = this.docTypes.asObservable();

  private _docSubtypes: DocSubtype[] = [];
  private docSubtypes = new BehaviorSubject(this._docSubtypes);
  public docSubtypes$ = this.docSubtypes.asObservable();

  private _taxReturnDifficultyLevels: TaxReturnDifficultyLevels[] = [];
  private taxReturnDifficultyLevels = new BehaviorSubject(
    this._taxReturnDifficultyLevels
  );
  public taxReturnDifficultyLevels$ =
    this.taxReturnDifficultyLevels.asObservable();

  private _docGroups: DocGroup[] = [];
  private docGroups = new BehaviorSubject(this._docGroups);
  public docGroups$ = this.docGroups.asObservable();

  private _docTypeSubtypes: DocTypeSubtype[] = [];
  private docTypeSubtypes = new BehaviorSubject(this._docTypeSubtypes);
  public docTypeSubtypes$ = this.docTypeSubtypes.asObservable();

  private _documentsLoading: boolean = false;
  private documentsLoading = new BehaviorSubject(this._documentsLoading);
  public documentsLoading$ = this.documentsLoading.asObservable();

  private _docStatuses: DocStatus[] = [];
  private docStatuses = new BehaviorSubject(this._docStatuses);
  public docStatuses$ = this.docStatuses.asObservable();

  public updatedDocument = new EventEmitter<VDocumentsFilteringTable>();

  private _documents: Paginator<VDocumentsFilteringTable, PaginatorInfo> = {
    data: [],
    paginatorInfo: {},
  };
  public documents = new BehaviorSubject(this._documents);
  public documents$ = this.documents.asObservable();

  private docSub: Subscription;
  private refreshDocQuery: QueryRef<
    DocumentRefreshResults,
    DocumentRefreshVariables
  >;

  private _page: number = 1;
  private page = new BehaviorSubject(this._page);
  public page$ = this.page.asObservable();

  private _filters: DocumentFilter = {};
  private filters = new BehaviorSubject(this._filters);
  public filters$ = this.filters.asObservable();

  private _where: WhereConditions | null = null;
  private where = new BehaviorSubject(this._where);
  public where$ = this.where.asObservable();

  private _order = defaultSort;
  private order = new BehaviorSubject(this._order);
  public order$ = this.order.asObservable();

  constructor(public apollo: Apollo, private userService: CurrentUserService, private _snackBar: DefaultSnackBarService) {}

  private _setDocumentsLoading(loading: boolean) {
    this._documentsLoading = loading;
    this.documentsLoading.next(this._documentsLoading);
  }
  private _setDocTypes(docTypes: DocType[]) {
    this._docTypes = docTypes;
    this.docTypes.next(this._docTypes);
  }
  private _setDocuments(
    documents: Paginator<VDocumentsFilteringTable, PaginatorInfo>
  ) {
    this._documents = documents;
    this.documents.next(this._documents);
  }
  private _setDocSubtypes(docSubtypes: DocSubtype[]) {
    this._docSubtypes = docSubtypes;
    this.docSubtypes.next(this._docSubtypes);
  }
  private _setDocTypeSubtypes(docTypeSubtypes: DocTypeSubtype[]) {
    this._docTypeSubtypes = docTypeSubtypes;
    this.docTypeSubtypes.next(this._docTypeSubtypes);
  }
  private _setDocGroups(docGroups: DocGroup[]) {
    this._docGroups = docGroups;
    this.docGroups.next(this._docGroups);
  }
  private _setDocStatuses(docStatuses: DocStatus[]) {
    this._docStatuses = docStatuses;
    this.docStatuses.next(this._docStatuses);
  }
  private _setTaxReturnDifficultyLevels(
    taxReturnDifficultyLevels: TaxReturnDifficultyLevels[]
  ) {
    this._taxReturnDifficultyLevels = taxReturnDifficultyLevels;
    this.taxReturnDifficultyLevels.next(this._taxReturnDifficultyLevels);
  }
  public updatePage(page: number) {
    this._page = page;
    this.page.next(this._page);
  }
  public updateFilters(filters: DocumentFilter) {
    this._filters = filters;
    this.filters.next(this._filters);
  }
  public updateWhere(where: WhereConditions | null) {
    this._where = where;
    this.where.next(this._where);
  }
  public fetchDocuments() {
    this._setDocumentsLoading(true);
    this.apollo
      .query({ query: FETCH_DOCUMENTS, variables: { filter:this._filters, where: this._where, order: this._order } })
      .subscribe(({ data }) => {
        this._setDocumentsLoading(false);
        if (!data) return;
        this._setDocuments(data.vDocumentsFilterTable);
        this._setDocGroups(data.docGroups);
        this._setDocTypes(data.docTypes);
        this._setDocSubtypes(data.docSubtypes);
        this._setDocTypeSubtypes(data.docTypeSubtypes);
        this._setDocStatuses(data.docStatuses);
        this._setTaxReturnDifficultyLevels(data.taxReturnDifficultyLevels);
      });
  }
  public setOrder(order: string | null) {
    switch (order) {
      case 'name':
        this._order = [{ column: 'OWNED_ENTITY_NAME', order: Direction.ASC }];
        break;
      case 'due_date':
        this._order = [{ column: 'DUE_DATE', order: Direction.ASC }];
        break;
      case 'followup_date':
        this._order = [{ column: 'LAST_FOLLOWUP_SENT', order: Direction.DESC }];
        break;
      case 'status_date':
        this._order = [
          { column: 'CURRENT_STATUS_DATE', order: Direction.DESC },
        ];
        break;
      case 'period':
        this._order = [{ column: 'DOC_DATE', order: Direction.DESC }];
        break;
      default:
        this._order = defaultSort;
    }
    this.order.next(this._order);
  }
  public refreshDocuments() {
    this._setDocumentsLoading(true);
    this.apollo
      .query({
        query: REFRESH_DOCUMENTS,
        variables: {
          page: this._page,
          filter: this._filters,
          where: this._where,
          order: this._order,
        },
        fetchPolicy: 'network-only',
      })
      .subscribe(({ data }) => {
        if (data) this._setDocuments(data.vDocumentsFilterTable);
        this._setDocumentsLoading(false);
      });
  }
  public fetchUpdatedVDocument(id: ID) {
    this._setDocumentsLoading(true);
    let sub = this.apollo
      .query({
        query: FETCH_DOCUMENT,
        variables: { id },
        fetchPolicy: 'network-only',
      })
      .subscribe(({ data }) => {
        if (data?.vDocumentsFilter) {
          this.updatedDocument.emit(data.vDocumentsFilter);
        }
        sub?.unsubscribe();
      });
  }
  public updateDocument(id: ID, input: UpdateDocumentInput) {
    this._setDocumentsLoading(true);
    let userSub = this.userService.user$.subscribe((user) => {
      if (user.id) input.user_id = user.id;
      let sub = this.apollo
        .mutate({
          mutation: UPDATE_DOCUMENT,
          variables: {
            id,
            input,
          },
        })
        .subscribe(({ data, loading }) => {
          if (data?.updateDocument?.id) {
            this._snackBar.open("Updated Document")
            this.fetchUpdatedVDocument(data.updateDocument.id);
          }
          this._setDocumentsLoading(false);
          sub?.unsubscribe();
        });
      userSub?.unsubscribe();
    });
  }
  public addNewNote(note_text: string, document_version_id: ID) {
    let userSub = this.userService.user$.subscribe((user) => {
      if (user?.id) {
        let sub = this.apollo
          .mutate({
            mutation: CREATE_NOTE,
            variables: {
              input: { note_text, document_version_id, user_id: user.id },
            },
          })
          .subscribe(({ data, loading }) => {
            if (
              data?.createDocumentVersionNote?.documentVersion?.document?.id
            ) {
              this.fetchUpdatedVDocument(
                data.createDocumentVersionNote.documentVersion.document.id
              );
            }
            sub?.unsubscribe();
          });
      }
      userSub?.unsubscribe();
    });
  }
  public deleteDocument(id: ID) {
    return this.apollo.mutate({ mutation: DELETE_DOCUMENT, variables: { id } });
  }
  public updateDueDate(
    document_id: ID,
    doc_subtype_id: ID | null,
    due_date: string
  ) {
    this._setDocumentsLoading(true);
    let userSub = this.userService.user$.subscribe((user) => {
      let variables: UpdateDueDateVariables = {
        document_id,
        doc_subtype_id,
        due_date,
        user_id: null,
      };
      if (user.id) {
        variables.user_id = user.id;
      }
      let sub = this.apollo
        .mutate({ mutation: UPDATE_DUE_DATE, variables })
        .subscribe(({ data, loading }) => {
          if (data?.updateDocDueDate) {
            this.fetchUpdatedVDocument(data.updateDocDueDate.document_id);
          }
          this._setDocumentsLoading(false);
          sub?.unsubscribe();
        });
      userSub?.unsubscribe();
    });
  }
  public createDocument(input: CreateDocumentInput) {
    return this.apollo.mutate({
      mutation: CREATE_DOCUMENT,
      variables: { input },
    });
  }
}
